import React, { useContext, useState } from 'react';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import Delete from '@material-ui/icons/Delete';
import ResetPasswordIcon from '@material-ui/icons/SettingsBackupRestore';
import {
  GetAllUsers,
  GetAllUsers_getAllUsers_users,
  GetAllUsersVariables,
} from '../../graphql/user/types/GetAllUsers';
import { REGENERATE_USER_PASSWORD } from '../../graphql/user/mutation';
import { useApolloClient, useMutation } from '@apollo/react-hooks';
import {
  RegenerateUserPassword,
  RegenerateUserPasswordVariables,
} from '../../graphql/user/types/RegenerateUserPassword';
import { displaySnackBar } from '../../common/displaySnackbar';
import ConfirmDialog from '../../common/ConfirmDialog';
import BlockUserCheckbox from './BlockUser/BlockUser';
import { Avatar, Typography } from '@material-ui/core';
import { formatRoles } from '../../utils/user';
import CreateNextCloudFolderButton from './CreateNextCloudFolderButton';
import SetUserIsManagerCheckBox from './SetUserIsManager/SetUserIsManagerCheckBox';
import UserAdditionalInfosModal from '../UserDetails/AdditionalInfos/UserAdditionalInfosModal';
import ManageEntitiesModal from '../UserDetails/ManageEntities/ManageEntitiesModal';
import { Column, Filter, Query } from 'material-table';
import MaterialTableComponent from '../../common/components/MaterialTableComponent';
import { materialTableLocaleData } from '../../locale/material-table';
import CustomDialog from '../../common/components/CustomDialog';
import UserForm from './UserForm';
import { IUserContext, UserContext } from '../../providers/UserProvider';
import { formatName, getInitials } from '../../utils/common';
import MTableSelectFilterComponent from '../../common/components/MaterialTableComponents/MTableSelectFilterComponent';
import MTableFilterComponent from '../../common/components/MaterialTableComponents/MTableFilterComponent';
import { Operation, Resource, Role, ROLES } from '../../common/constants';
import defaultImage from '../../assets/defaultAvatar.png';
import EditIcon from '@material-ui/icons/Edit';
import { GET_ALL_USERS } from '../../graphql/user/query';
import MessageIcon from '@material-ui/icons/Message';
import DiscussionMessagesComponent from '../DiscussionMessages';
import GetAppIcon from '@material-ui/icons/GetApp';
import MobileFriendlyIcon from '@material-ui/icons/MobileFriendly';
import MobileOffIcon from '@material-ui/icons/MobileOff';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import UserPermissionsManager from '../UserPermissionsManager';
import {
  BusinessWhereInput,
  GetAllUsersInput,
  SortOrder,
  UserOrderByInput,
} from '../../types/graphql-global-types';
import { API_URL } from '../../config';
import ConfirmDeleteModal from '../../common/components/ConfirmationModal';
import useLogic from './useLogic';

const Users = () => {
  const { editUser, loading } = useLogic();
  const { canI } = useContext<IUserContext>(UserContext);
  const client = useApolloClient();

  const [deleteDialog, setDeleteDialog] = useState<boolean>(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [userManagerId, setUserManagerId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openCreateUserModal, setOpenCreateUserModal] =
    useState<boolean>(false);
  const [userAdditionalInfosModalOpened, setUserAdditionalInfosModalOpened] =
    useState<boolean>(false);
  const [chatroomOpened, setChatroomOpened] = useState<boolean>(false);
  const [openConfirmDialog, setopenConfirmDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<
    GetAllUsers_getAllUsers_users | undefined
  >(undefined);
  const [permissionsManagerOpened, setPermissionsManagerOpened] =
    useState<boolean>(false);

  const description =
    'Êtes-vous sûr de réinitialiser le mot de passe de cet utilisateur ?';

  const confirmDialogTitle = 'Confirmation';

  const handleOpenResetPasswordConfirmDialog = () => {
    setopenConfirmDialog(true);
  };

  const handleCancel = () => {
    setopenConfirmDialog(false);
  };
  const handleConfirmDelete = async () => {
    if (userId)
      await editUser({ variables: { input: { userId, isDeleted: true } } });
    setDeleteDialog(false);
  };
  const [regenerateUserPasswordMutation] = useMutation<
    RegenerateUserPassword,
    RegenerateUserPasswordVariables
  >(REGENERATE_USER_PASSWORD, {
    onCompleted: () => {
      displaySnackBar(client, {
        type: 'SUCCESS',
        isOpen: true,
        message: "Mot de passe regénéré et envoyé à l'utilisateur.",
      });
    },
  });

  const canCreateUser = canI(Operation.CREATE, Resource.USERS);
  const canEditUser = canI(Operation.UPDATE, Resource.USERS);
  const canExportUsers = canI(Operation.EXPORT, Resource.USERS);
  const canAnswerFeedback = canI(Operation.CREATE, Resource.FEEDBACKS);
  const canEditRoles = canI(Operation.UPDATE, Resource.ROLES);

  const tableColumns = [
    {
      title: 'Mobile',
      field: 'isMobile',
      filtering: false,
      searchable: false,
      sorting: false,
      render: (user) =>
        !!user?.isMobile ? (
          <MobileFriendlyIcon />
        ) : (
          <MobileOffIcon color="disabled" />
        ),
    },
    {
      title: 'idCC',
      editable: 'never',
      field: 'id',
      searchable: false,
      width: 80,
      filterComponent: ({ columnDef, onFilterChanged }: any) => (
        <MTableFilterComponent
          onChange={(value) => onFilterChanged(columnDef?.tableData?.id, value)}
        />
      ),
    },
    {
      title: 'idCRM',
      editable: 'never',
      field: 'idCRM',
      searchable: false,
      width: 80,
      filterComponent: ({ columnDef, onFilterChanged }: any) => (
        <MTableFilterComponent
          onChange={(value) => onFilterChanged(columnDef?.tableData?.id, value)}
        />
      ),
    },
    {
      title: 'Date',
      field: 'createdAt',
      editable: 'never',
      type: 'date',
      defaultGroupSort: 'desc',
      defaultSort: 'desc',
      searchable: false,
    },
    {
      title: 'Photo de profil',
      field: 'avatarUrl',
      searchable: false,
      filtering: false,
      sorting: false,
      render: (user) => (
        <Avatar
          alt={getInitials(`${user?.firstName ?? ''} ${user?.lastName ?? ''}`)}
          src={user?.avatarUrl || defaultImage}
        />
      ),
    },
    {
      title: 'Nom',
      field: 'firstName',
      filtering: false,
      render: (user) => `${user?.firstName ?? ''} ${user?.lastName ?? ''}`,
    },
    {
      title: 'Email',
      field: 'email',
      filtering: false,
    },
    {
      title: 'Téléphone',
      field: 'phone',
      filtering: false,
    },
    {
      title: 'Rôle(s)',
      field: 'roles',
      filtering: true,
      searchable: false,
      sorting: false,
      filterComponent: ({ columnDef, onFilterChanged }: any) => {
        return (
          <MTableSelectFilterComponent
            options={Object.keys(Role)}
            labels={ROLES}
            onChange={(value) =>
              onFilterChanged(columnDef?.tableData?.id, value)
            }
          />
        );
      },
      render: (user) => `${formatRoles(user.roles)}`,
    },
    {
      title: 'Enseigne',
      field: 'enseigneName',
      emptyValue: '-',
      searchable: false,
      filtering: true,
      filterComponent: ({ columnDef, onFilterChanged }: any) => (
        <MTableFilterComponent
          onChange={(value) => onFilterChanged(columnDef?.tableData?.id, value)}
        />
      ),
    },
    {
      title: 'Entité(s)',
      field: 'entities',
      emptyValue: '-',
      searchable: false,
      filtering: true,
      sorting: false,
      filterComponent: ({ columnDef, onFilterChanged }: any) => (
        <MTableFilterComponent
          onChange={(value) => onFilterChanged(columnDef?.tableData?.id, value)}
        />
      ),
      render: (user) =>
        user?.entities?.map((entity) => entity.name)?.join(', '),
    },
    {
      title: 'Superviseur',
      field: 'isManager',
      filtering: false,
      searchable: false,
      render: (user) => (
        <SetUserIsManagerCheckBox
          id={user.id}
          isManager={user.isManager}
          name={user.firstName}
          setUserManagerId={setUserManagerId}
          disabled={!canEditUser}
        />
      ),
    },
    {
      title: 'Réinitialiser le mot de passe',
      filtering: false,
      searchable: false,
      sorting: false,
      render: (user) => (
        <ResetPasswordIcon
          style={canEditUser ? { cursor: 'pointer' } : { color: 'grey' }}
          onClick={() => {
            if (canEditUser) {
              handleOpenResetPasswordConfirmDialog();
              setSelectedUser(user);
            }
          }}
        />
      ),
    },
    {
      title: 'Bloqué',
      field: 'bloqued',
      filtering: false,
      searchable: false,
      render: (user) => (
        <BlockUserCheckbox
          id={user.id}
          blocked={user.blocked}
          name={user.firstName}
          disabled={!canEditUser}
        />
      ),
    },
    {
      title: 'NextCloud Token',
      field: 'directoryToken',
      filtering: false,
      searchable: false,
      sorting: false,
      render: (user) => (
        <CreateNextCloudFolderButton
          userId={user.id}
          directoryToken={user.directoryToken}
          disabled={!canEditUser}
        />
      ),
    },
  ] as Array<Column<GetAllUsers_getAllUsers_users>>;

  return (
    <>
      <MaterialTableComponent
        title="Liste des utilisateurs"
        columns={tableColumns}
        data={(query) =>
          new Promise(async (resolve) => {
            const { page, pageSize } = query;
            const search = query?.search?.trim() || undefined;

            const filters = query?.filters?.reduce<BusinessWhereInput>(
              (acc, filter) => ({
                ...acc,
                ...formatFilter(filter),
              }),
              {},
            );

            const { data } = await client.query<
              GetAllUsers,
              GetAllUsersVariables
            >({
              fetchPolicy: 'network-only',
              query: GET_ALL_USERS,
              variables: {
                input: {
                  take: pageSize,
                  skip: page * pageSize,
                  where: {
                    name: search,
                    phone: search,
                    email: search,
                    ...filters,
                  },
                  orderBy: formatOrderBy(query),
                },
              },
            });

            const totalCount = data?.getAllUsers?.totalCount ?? 0;
            const businesses = data?.getAllUsers?.users ?? [];

            resolve({
              totalCount,
              page,
              data: businesses,
            });
          })
        }
        isLoading={false}
        localization={materialTableLocaleData.fr}
        options={{
          grouping: false,
          filtering: true,
          search: true,
          pageSize: 10,
        }}
        actions={[
          {
            icon: () => <GetAppIcon />,
            onClick: (_, __) => {
              window.open(API_URL + '/api/v1/download/users');
            },
            isFreeAction: true,
            tooltip: 'Exporter sous Excel',
            disabled: !canExportUsers,
          },
          {
            icon: () => <PersonAddIcon />,
            onClick: (_, __) => {
              setOpenCreateUserModal(true);
            },
            isFreeAction: true,
            tooltip: 'Créer un nouvel utilisateur',
            disabled: !canCreateUser,
          },
          {
            icon: () => <EditIcon />,
            tooltip: 'Editer',
            onClick: (event, rowData) => {
              setUserId(rowData.id);
              setUserAdditionalInfosModalOpened(true);
            },
            disabled: !canEditUser,
          },
          {
            icon: () => <Delete />,
            onClick: (event, rowData) => {
              setUserId(rowData.id);
              setDeleteDialog(true);
            },

            tooltip: 'Supprimer un utilisateur',
            disabled: !canEditUser,
          },
          {
            icon: () => <MessageIcon />,
            tooltip: 'Envoyer message',
            onClick: async (event, user) => {
              setSelectedUser(user);
              setChatroomOpened(true);
            },
            disabled: !canAnswerFeedback,
          },
          (user: GetAllUsers_getAllUsers_users) => ({
            icon: () => <VpnKeyIcon />,
            tooltip: 'Gérer les permissions',
            onClick: async (event, user) => {
              setSelectedUser(user);
              setPermissionsManagerOpened(true);
            },
            disabled: !canEditRoles || user.id === 1,
          }),
        ]}
      />
      <ConfirmDialog
        {...{
          open: openConfirmDialog,
          handleCancel,
          title: confirmDialogTitle,
          description,
          handleOK: () => {
            const id = selectedUser?.id;
            id && regenerateUserPasswordMutation({ variables: { id } });
            setopenConfirmDialog(false);
          },
        }}
      />
      {userAdditionalInfosModalOpened && userId && (
        <UserAdditionalInfosModal
          userId={userId}
          setUserId={setUserId}
          handleClose={() => setUserAdditionalInfosModalOpened(false)}
        />
      )}
      {userManagerId && (
        <ManageEntitiesModal
          userId={userManagerId}
          setUserId={setUserManagerId}
        />
      )}

      <CustomDialog
        fullWidth
        maxWidth="sm"
        loading={isLoading}
        title="Création utilisateur"
        open={openCreateUserModal}>
        <UserForm
          setIsLoading={setIsLoading}
          onCreate={() => setOpenCreateUserModal(false)}
          onCancel={() => setOpenCreateUserModal(false)}
        />
      </CustomDialog>

      <CustomDialog
        fullWidth
        loading={isLoading}
        title={`Envoi message à ${formatName({
          firstName: selectedUser?.firstName,
          lastName: selectedUser?.lastName,
        })}`}
        open={chatroomOpened}
        onClose={() => {
          setChatroomOpened(false);
          setSelectedUser(undefined);
        }}>
        <DiscussionMessagesComponent
          userIds={selectedUser && [1, selectedUser.id]}
        />
      </CustomDialog>

      <UserPermissionsManager
        open={permissionsManagerOpened}
        title={`Gestion des permissions de ${formatName({
          firstName: selectedUser?.firstName,
          lastName: selectedUser?.lastName,
        })}`}
        user={selectedUser}
        onClose={() => {
          setPermissionsManagerOpened(false);
          setSelectedUser(undefined);
        }}
      />
      <ConfirmDeleteModal
        open={deleteDialog}
        content={
          <Typography>
            Voulez-vous vraiment supprimer cet utilisateur ?
          </Typography>
        }
        confirmLabel="Oui, supprimer"
        onCancel={() => setDeleteDialog(false)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

const formatFilter = (filter: Filter<any>): GetAllUsersInput => {
  const key = filter?.column?.field as string;
  const value = filter?.value || undefined;

  const _parseNumber = (n: string | null | undefined): number | undefined =>
    n && !isNaN(+n) ? +n : undefined;

  const mapping: { [p: string]: any } = {
    id: { idCC: _parseNumber(value) },
    idCRM: { idCRM: _parseNumber(value) },
    firstName: { name: value },
    roles: { roles: [value] },
  };

  return mapping[key] || { [key]: value };
};

const formatOrderBy = (query: Query<any>): UserOrderByInput | undefined => {
  const key = query?.orderBy?.field;
  const direction = query?.orderDirection as SortOrder;

  if (!key || !direction) return undefined;

  const mapping: { [p: string]: any } = {
    id: { idCC: direction },
    enseigneName: { enseigne: { name: direction } },
    bloqued: { isBlocked: direction },
  };

  return mapping[key as string] || { [key]: direction };
};

export default Users;
